import type { PlanCode, UserRole } from '$lib/api/types';

export type RouteDomain =
	| 'today'
	| 'people'
	| 'time'
	| 'documents'
	| 'performance'
	| 'operations'
	| 'admin';
export type NavDepth = 0 | 1;
export type RouteSurface =
	| 'landing'
	| 'login'
	| 'signup'
	| 'invite-signup'
	| 'forgot-password'
	| 'reset-password'
	| 'certificate-verify'
	| 'company-setup'
	| 'resource';

export type RouteParams = Record<string, string>;

export type RouteDefinition = {
	pattern: string;
	title: string;
	description: string;
	domain: RouteDomain;
	surface?: RouteSurface;
	public?: boolean;
	admin?: boolean;
	owner?: boolean;
	employee?: boolean;
	superAdmin?: boolean;
	nav?: boolean;
	navParent?: string;
	navOrder?: number;
	navDepth?: NavDepth;
	/** 이 항목을 노출하기 위한 최소 요금제. 미지정이면 모든 요금제(FREE 포함)에서 노출되는 핵심 기능. */
	minPlan?: PlanCode;
	endpoint?: string | ((params: RouteParams) => string);
};

export type NavigationDomainDefinition = {
	domain: RouteDomain;
	label: string;
	defaultOpen: boolean;
};

export type NavigationSection = NavigationDomainDefinition & {
	routes: RouteDefinition[];
};

export type MatchedRoute = {
	definition: RouteDefinition;
	params: RouteParams;
};

const currentYearQuery = () => `year=${new Date().getFullYear()}`;

export const PRODUCT_NAV_DOMAINS: NavigationDomainDefinition[] = [
	{ domain: 'today', label: '오늘 할 일', defaultOpen: true },
	{ domain: 'people', label: '사람/조직', defaultOpen: true },
	{ domain: 'time', label: '근무/휴가', defaultOpen: true },
	{ domain: 'documents', label: '결재/문서', defaultOpen: true },
	{ domain: 'performance', label: '성과/보상', defaultOpen: false },
	{ domain: 'operations', label: '운영/설정', defaultOpen: false }
];

const ADMIN_NAV_DOMAINS: NavigationDomainDefinition[] = [
	{ domain: 'admin', label: '운영', defaultOpen: true }
];

const DOMAIN_ORDER: Record<RouteDomain, number> = {
	today: 0,
	people: 1,
	time: 2,
	documents: 3,
	performance: 4,
	operations: 5,
	admin: 6
};

const PLAN_RANK: Record<PlanCode, number> = {
	FREE: 0,
	BASIC: 1,
	STANDARD: 2,
	PRO: 3
};

function planRank(plan: string | null | undefined): number {
	if (plan && plan in PLAN_RANK) return PLAN_RANK[plan as PlanCode];
	return PLAN_RANK.FREE;
}

/**
 * 요금제 기준 노출 가능 여부.
 * - plan === undefined: 게이팅 미적용(레거시/테스트 경로 — 모두 노출).
 * - plan === null: 회사 정보 미로딩 → 보수적으로 FREE 취급(고급 기능 숨김).
 * - 그 외: 요금제 등급으로 minPlan과 비교.
 */
function routeMeetsPlan(route: RouteDefinition, plan: string | null | undefined): boolean {
	if (plan === undefined) return true;
	if (!route.minPlan) return true;
	return planRank(plan) >= PLAN_RANK[route.minPlan];
}

const MOBILE_NAV_PATTERNS: Record<UserRole, string[]> = {
	SUPER_ADMIN: ['/admin', '/admin/companies', '/admin/payments', '/admin/usage'],
	OWNER: ['/dashboard', '/employees', '/attendance', '/approvals'],
	ADMIN: ['/dashboard', '/employees', '/attendance', '/approvals'],
	EMPLOYEE: ['/home', '/attendance', '/leave', '/notifications']
};

export const PUBLIC_PATHS = new Set([
	'/',
	'/login',
	'/signup',
	'/signup/invite',
	'/certificates/verify',
	'/forgot-password',
	'/reset-password'
]);

export const ROUTES: RouteDefinition[] = [
	{
		pattern: '/',
		title: 'AntFlow',
		description: '근태, 휴가, 결재, 급여를 한 흐름으로 관리합니다.',
		domain: 'today',
		surface: 'landing',
		public: true
	},
	{
		pattern: '/login',
		title: '로그인',
		description: 'AntFlow 계정으로 로그인합니다.',
		domain: 'today',
		surface: 'login',
		public: true
	},
	{
		pattern: '/signup',
		title: '회원가입',
		description: '새 회사를 시작하거나 초대받은 계정으로 가입합니다.',
		domain: 'today',
		surface: 'signup',
		public: true
	},
	{
		pattern: '/signup/invite',
		title: '초대 가입',
		description: '초대 토큰으로 회사에 합류합니다.',
		domain: 'today',
		surface: 'invite-signup',
		public: true,
		endpoint: '/auth/signup/invite'
	},
	{
		pattern: '/forgot-password',
		title: '비밀번호 찾기',
		description: '비밀번호 재설정 메일을 요청합니다.',
		domain: 'today',
		surface: 'forgot-password',
		public: true
	},
	{
		pattern: '/reset-password',
		title: '비밀번호 재설정',
		description: '메일로 받은 토큰으로 비밀번호를 변경합니다.',
		domain: 'today',
		surface: 'reset-password',
		public: true
	},
	{
		pattern: '/certificates/verify',
		title: '증명서 검증',
		description: '발급된 증명서의 진위를 확인합니다.',
		domain: 'documents',
		surface: 'certificate-verify',
		public: true
	},
	{
		pattern: '/home',
		title: '홈',
		description: '오늘 내 근무와 알림을 확인합니다.',
		domain: 'today',
		nav: true,
		navOrder: 10,
		employee: true,
		endpoint: '/dashboard'
	},
	{
		pattern: '/dashboard',
		title: '대시보드',
		description: '팀 현황과 처리할 일을 확인합니다.',
		domain: 'today',
		nav: true,
		navOrder: 10,
		admin: true,
		endpoint: '/dashboard'
	},
	{
		pattern: '/insights/hr',
		title: '인사이트',
		description: '모바일 HR 운영 지표와 리스크를 확인합니다.',
		domain: 'today',
		nav: true,
		navOrder: 20,
		admin: true,
		minPlan: 'PRO',
		endpoint: '/app/insights/hr'
	},
	{
		pattern: '/agents',
		title: '에이전트',
		description: 'AI 에이전트, 지식, 권한, 자동화를 관리합니다.',
		domain: 'operations',
		nav: true,
		navOrder: 10,
		admin: true,
		minPlan: 'PRO',
		endpoint: '/agents/operations/summary'
	},
	{
		pattern: '/employees',
		title: '직원',
		description: '직원 정보, 초대, 인사 프로필을 관리합니다.',
		domain: 'people',
		nav: true,
		navOrder: 10,
		admin: true,
		endpoint: '/employees?page=0&size=20'
	},
	{
		pattern: '/employees/number-policy',
		title: '사번 정책',
		description: '사번 자동생성과 수정 잠금을 설정합니다.',
		domain: 'people',
		nav: true,
		navParent: '/employees',
		navOrder: 15,
		navDepth: 1,
		admin: true,
		endpoint: '/employees/number-policy'
	},
	{
		pattern: '/employees/:id',
		title: '직원 상세',
		description: '직원 인사 기록과 계약 정보를 확인합니다.',
		domain: 'people',
		admin: true,
		endpoint: ({ id }) => `/employees/${id}`
	},
	{
		pattern: '/attendance',
		title: '출퇴근',
		description: '근무 기록과 출퇴근 상태를 관리합니다.',
		domain: 'time',
		nav: true,
		navOrder: 10,
		endpoint: '/attendances/today'
	},
	{
		pattern: '/shifts',
		title: '교대근무',
		description: '교대근무 스케줄, 공유 상태, 출퇴근 완료와 인건비 리스크를 관리합니다.',
		domain: 'time',
		nav: true,
		navOrder: 20,
		admin: true,
		minPlan: 'PRO',
		endpoint: '/field-operations/summary'
	},
	{
		pattern: '/field-operations',
		title: '현장 운영',
		description: '매장 스케줄, 위치 출퇴근, 인건비와 준법 리스크를 관리합니다.',
		domain: 'time',
		nav: true,
		navOrder: 30,
		admin: true,
		minPlan: 'PRO',
		endpoint: '/field-operations/summary'
	},
	{
		pattern: '/partners/advisory',
		title: '파트너 자문',
		description: 'HR Partners와 Payroll Partners 수준의 자문 요청, 배정, 해결 현황을 관리합니다.',
		domain: 'operations',
		nav: true,
		navOrder: 80,
		navDepth: 1,
		admin: true,
		minPlan: 'PRO',
		endpoint: '/partners/advisory/summary'
	},
	{
		pattern: '/customer-success',
		title: '고객 성공',
		description: '사용 가이드, 업데이트, 아카데미, 공지, 제품 인터뷰와 지원 티켓을 관리합니다.',
		domain: 'operations',
		nav: true,
		navOrder: 70,
		navDepth: 1,
		admin: true,
		minPlan: 'PRO',
		endpoint: '/customer-success/summary'
	},
	{
		pattern: '/engagement',
		title: '몰입',
		description: '펄스 설문, 인정·칭찬, 피드백 루틴과 리텐션 신호를 관리합니다.',
		domain: 'people',
		nav: true,
		navOrder: 60,
		admin: true,
		minPlan: 'PRO',
		endpoint: '/engagement/summary'
	},
	{
		pattern: '/news-feed',
		title: '사내 소식',
		description: '전사 공지, 정책 안내, 컬처 이벤트와 읽음·확인 상태를 관리합니다.',
		domain: 'today',
		nav: true,
		navOrder: 30,
		endpoint: '/news-feed/summary'
	},
	{
		pattern: '/employee-lifecycle',
		title: '입퇴사',
		description: '입사·퇴사 체크리스트, 담당자, 마감일, 진행상황을 관리합니다.',
		domain: 'people',
		nav: true,
		navOrder: 30,
		admin: true,
		minPlan: 'STANDARD',
		endpoint: '/employee-lifecycle/summary'
	},
	{
		pattern: '/learning',
		title: '교육',
		description: '교육 프로그램, 수강 배정, 수료, 필수교육 리스크를 관리합니다.',
		domain: 'people',
		nav: true,
		navOrder: 50,
		admin: true,
		minPlan: 'PRO',
		endpoint: '/learning/summary'
	},
	{
		pattern: '/organization',
		title: '조직',
		description: '부서 구조, 관리자, 직원 배정을 관리합니다.',
		domain: 'people',
		nav: true,
		navOrder: 20,
		admin: true,
		endpoint: '/organization/departments'
	},
	{
		pattern: '/organization/insights',
		title: '조직 인사이트',
		description: '조직도 트렌드, 유지율, 목표 달성, 초과근무, 휴가 사용을 분석합니다.',
		domain: 'people',
		nav: true,
		navParent: '/organization',
		navOrder: 25,
		navDepth: 1,
		admin: true,
		minPlan: 'STANDARD',
		endpoint: '/organization/insights/summary'
	},
	{
		pattern: '/performance/goals',
		title: '목표',
		description: '회사 목표, 핵심결과, 진행률 리스크를 관리합니다.',
		domain: 'performance',
		nav: true,
		navOrder: 10,
		admin: true,
		minPlan: 'PRO',
		endpoint: '/performance/goals'
	},
	{
		pattern: '/performance/reviews',
		title: '평가',
		description: '성과평가 사이클, 대상자, 목표 연동 평가를 관리합니다.',
		domain: 'performance',
		nav: true,
		navOrder: 20,
		admin: true,
		minPlan: 'PRO',
		endpoint: '/performance/reviews'
	},
	{
		pattern: '/performance/one-on-ones',
		title: '원온원',
		description: '원온원 일정, 체크인, 노트, 액션 아이템을 관리합니다.',
		domain: 'performance',
		nav: true,
		navOrder: 30,
		admin: true,
		minPlan: 'PRO',
		endpoint: '/performance/one-on-ones'
	},
	{
		pattern: '/meetings',
		title: '미팅',
		description: '미팅 노트, 결정사항, 액션 아이템과 캘린더 동기화를 관리합니다.',
		domain: 'performance',
		nav: true,
		navOrder: 40,
		admin: true,
		minPlan: 'PRO',
		endpoint: '/meetings/summary'
	},
	{
		pattern: '/recruiting',
		title: '채용',
		description: '채용 공고, 후보자 전형, 평가와 최종 합격을 관리합니다.',
		domain: 'people',
		nav: true,
		navOrder: 40,
		admin: true,
		minPlan: 'PRO',
		endpoint: '/recruiting/candidates'
	},
	{
		pattern: '/leave',
		title: '휴가',
		description: '휴가 신청과 잔여일을 확인합니다.',
		domain: 'time',
		nav: true,
		navOrder: 40,
		endpoint: '/leave/requests/page?page=0&size=20'
	},
	{
		pattern: '/expenses',
		title: '경비',
		description: '경비 신청과 처리 현황을 확인합니다.',
		domain: 'documents',
		nav: true,
		navOrder: 60,
		endpoint: '/expenses?page=0&size=20'
	},
	{
		pattern: '/certificates',
		title: '증명서',
		description: '재직·경력증명서 신청과 발급 내역을 관리합니다.',
		domain: 'documents',
		nav: true,
		navOrder: 50,
		endpoint: '/certificates?page=0&size=20'
	},
	{
		pattern: '/workflows',
		title: '워크플로우',
		description: '자유 양식 문서와 결재 진행 현황을 관리합니다.',
		domain: 'documents',
		nav: true,
		navOrder: 20,
		minPlan: 'STANDARD',
		endpoint: '/workflows/documents'
	},
	{
		pattern: '/digital-contracts',
		title: '전자계약',
		description: '근로계약 템플릿과 전자 서명 요청을 관리합니다.',
		domain: 'documents',
		nav: true,
		navOrder: 30,
		admin: true,
		minPlan: 'STANDARD',
		endpoint: '/digital-contracts/requests'
	},
	{
		pattern: '/approvals',
		title: '결재',
		description: '승인 대기와 결재 내역을 확인합니다.',
		domain: 'documents',
		nav: true,
		navOrder: 10,
		endpoint: '/approvals/company?page=0&size=20'
	},
	{
		pattern: '/approvals/:id',
		title: '결재 상세',
		description: '결재 문서와 승인 단계를 확인합니다.',
		domain: 'documents',
		endpoint: ({ id }) => `/approvals/${id}`
	},
	{
		pattern: '/payroll',
		title: '급여',
		description: '급여명세서와 지급 설정을 관리합니다.',
		domain: 'performance',
		nav: true,
		navOrder: 50,
		admin: true,
		endpoint: '/payroll/settings'
	},
	{
		pattern: '/payroll/imports',
		title: '급여 가져오기',
		description: '기존 급여대장·명세서 파일을 AI로 추출해 과거 명세서로 등록합니다.',
		domain: 'performance',
		nav: true,
		navParent: '/payroll',
		navOrder: 55,
		navDepth: 1,
		admin: true,
		endpoint: '/payroll/imports'
	},
	{
		pattern: '/payroll/imports/:id',
		title: '급여 가져오기 상세',
		description: '추출된 행을 검토하고 과거 명세서로 등록합니다.',
		domain: 'performance',
		admin: true,
		endpoint: ({ id }) => `/payroll/imports/${id}`
	},
	{
		pattern: '/year-end-settlement',
		title: '연말정산',
		description: '연말정산 대상자, 공제자료, 환급·징수 결과를 관리합니다.',
		domain: 'performance',
		nav: true,
		navOrder: 60,
		admin: true,
		minPlan: 'STANDARD',
		endpoint: () => `/payroll/year-end-settlements/summary?${currentYearQuery()}`
	},
	{
		pattern: '/group-insurance',
		title: '단체보험',
		description: '임직원 단체보험 플랜, 가입 대상자, 월 납입 현황을 관리합니다.',
		domain: 'performance',
		nav: true,
		navOrder: 70,
		admin: true,
		minPlan: 'STANDARD',
		endpoint: '/benefits/group-insurance/summary'
	},
	{
		pattern: '/payroll/payslips',
		title: '명세서 목록',
		description: '회사 전체 급여명세서를 검색합니다.',
		domain: 'performance',
		nav: true,
		navParent: '/payroll',
		navOrder: 52,
		navDepth: 1,
		admin: true,
		endpoint: '/payroll/payslips/search'
	},
	{
		pattern: '/payroll/payslips/:employeeId',
		title: '직원 급여명세서',
		description: '직원의 전 기간 급여명세서를 확인합니다.',
		domain: 'performance',
		admin: true,
		endpoint: ({ employeeId }) => `/employees/${employeeId}/payslips`
	},
	{
		pattern: '/payroll/payslips/:employeeId/:payslipId',
		title: '급여명세서 상세',
		description: '개별 급여명세서 산출 내역을 확인합니다.',
		domain: 'performance',
		admin: true,
		endpoint: ({ employeeId, payslipId }) => `/employees/${employeeId}/payslips/${payslipId}`
	},
	{
		pattern: '/reports',
		title: '리포트',
		description: '근태, 휴가, 컴플라이언스 리포트를 확인합니다.',
		domain: 'performance',
		nav: true,
		navOrder: 80,
		admin: true,
		endpoint: '/reports/weekly-compliance'
	},
	{
		pattern: '/account/payslips/:employeeId/:payslipId',
		title: '급여명세서 상세',
		description: '본인의 발행된 급여명세서를 확인합니다.',
		domain: 'performance',
		endpoint: ({ employeeId, payslipId }) => `/employees/${employeeId}/payslips/${payslipId}`
	},
	{
		pattern: '/employees/:employeeId/payslips/:payslipId',
		title: '급여명세서 상세',
		description: '기존 발행 알림의 급여명세서를 확인합니다.',
		domain: 'performance',
		endpoint: ({ employeeId, payslipId }) => `/employees/${employeeId}/payslips/${payslipId}`
	},
	{
		pattern: '/account/profile',
		title: '내 정보',
		description: '내 인사정보를 확인합니다.',
		domain: 'people',
		nav: true,
		navOrder: 90,
		endpoint: '/employees/me'
	},
	{
		pattern: '/account/payslips',
		title: '내 급여',
		description: '내 급여명세서를 확인합니다.',
		domain: 'performance',
		nav: true,
		navOrder: 90,
		endpoint: '/employees/me'
	},
	{
		pattern: '/account/contracts',
		title: '내 전자계약',
		description: '근로계약서와 입사 서류를 확인하고 전자서명합니다.',
		domain: 'documents',
		nav: true,
		navOrder: 90,
		endpoint: '/digital-contracts/requests/me'
	},
	{
		pattern: '/account/password',
		title: '비밀번호 변경',
		description: '현재 계정의 비밀번호를 변경합니다.',
		domain: 'operations',
		endpoint: '/auth/me'
	},
	{
		pattern: '/notifications',
		title: '알림',
		description: '읽지 않은 알림과 변경 사항을 확인합니다.',
		domain: 'today',
		nav: true,
		navOrder: 40,
		endpoint: '/notifications?page=0&size=20'
	},
	{
		pattern: '/leave/admin',
		title: '잔여 관리',
		description: '직원별 휴가 잔여를 관리합니다.',
		domain: 'time',
		nav: true,
		navParent: '/leave',
		navOrder: 50,
		navDepth: 1,
		admin: true,
		endpoint: '/employees?page=0&size=200'
	},
	{
		pattern: '/leave/policy',
		title: '휴가 정책',
		description: '연차 부여와 이월 기준을 설정합니다.',
		domain: 'time',
		nav: true,
		navParent: '/leave',
		navOrder: 60,
		navDepth: 1,
		admin: true,
		endpoint: '/admin/leave/policy'
	},
	{
		pattern: '/approval-lines',
		title: '결재선',
		description: '승인자와 결재 단계를 관리합니다.',
		domain: 'documents',
		nav: true,
		navParent: '/approvals',
		navOrder: 40,
		navDepth: 1,
		admin: true,
		endpoint: '/approval-lines'
	},
	{
		pattern: '/work-policies',
		title: '근태 정책',
		description: '근무 시간과 출퇴근 기준을 설정합니다.',
		domain: 'time',
		nav: true,
		navParent: '/attendance',
		navOrder: 70,
		navDepth: 1,
		admin: true,
		endpoint: '/work-policies'
	},
	{
		pattern: '/holidays',
		title: '공휴일',
		description: '회사 휴무일을 관리합니다.',
		domain: 'time',
		nav: true,
		navParent: '/attendance',
		navOrder: 80,
		navDepth: 1,
		admin: true,
		endpoint: () => `/holidays?${currentYearQuery()}`
	},
	{
		pattern: '/security',
		title: '보안',
		description: '권한, 세션, 감사 로그, 보안 통제 상태를 점검합니다.',
		domain: 'operations',
		nav: true,
		navOrder: 20,
		navDepth: 1,
		admin: true,
		endpoint: '/security/operations/summary'
	},
	{
		pattern: '/open-api',
		title: 'Open API',
		description: '외부 연동 Client Credential과 접근 허용 IP를 관리합니다.',
		domain: 'operations',
		nav: true,
		navOrder: 30,
		navDepth: 1,
		admin: true,
		endpoint: '/open-api/clients'
	},
	{
		pattern: '/company/setup',
		title: '회사 설정 시작',
		description: '회사 정보를 등록해 AntFlow 사용을 시작합니다.',
		domain: 'operations',
		surface: 'company-setup',
		endpoint: '/companies/me'
	},
	{
		pattern: '/company/settings',
		title: '회사',
		description: '회사 정보와 운영 기준을 관리합니다.',
		domain: 'operations',
		nav: true,
		navOrder: 40,
		navDepth: 1,
		admin: true,
		endpoint: '/companies/me'
	},
	{
		pattern: '/billing',
		title: '구독',
		description: '요금제와 결제 내역을 관리합니다.',
		domain: 'operations',
		nav: true,
		navOrder: 50,
		navDepth: 1,
		admin: true,
		owner: true,
		endpoint: '/billing/subscription'
	},
	{
		pattern: '/billing/checkout',
		title: '결제 요청',
		description: '구독 결제 요청을 생성합니다.',
		domain: 'operations',
		owner: true,
		endpoint: '/billing/subscription'
	},
	{
		pattern: '/billing/payments/:id',
		title: '결제 상세',
		description: '결제 요청 처리 상태를 확인합니다.',
		domain: 'operations',
		owner: true,
		endpoint: ({ id }) => `/billing/payments/${id}`
	},
	{
		pattern: '/admin',
		title: '운영 콘솔',
		description: '전체 회사와 사용 현황을 관리합니다.',
		domain: 'admin',
		nav: true,
		navOrder: 10,
		superAdmin: true,
		endpoint: '/admin/usage'
	},
	{
		pattern: '/admin/companies',
		title: '회사 관리',
		description: '입점 회사와 구독 상태를 확인합니다.',
		domain: 'admin',
		nav: true,
		navOrder: 20,
		superAdmin: true,
		endpoint: '/admin/companies?page=0&size=20'
	},
	{
		pattern: '/admin/companies/:id',
		title: '회사 상세',
		description: '회사 상태와 운영 정보를 확인합니다.',
		domain: 'admin',
		superAdmin: true,
		endpoint: ({ id }) => `/admin/companies/${id}`
	},
	{
		pattern: '/admin/payments',
		title: '결제 승인',
		description: '무통장 입금 결제 요청을 검토합니다.',
		domain: 'admin',
		nav: true,
		navOrder: 30,
		superAdmin: true,
		endpoint: '/admin/payments?page=0&size=20'
	},
	{
		pattern: '/admin/payments/:id',
		title: '결제 승인 상세',
		description: '결제 요청을 승인하거나 반려합니다.',
		domain: 'admin',
		superAdmin: true,
		endpoint: ({ id }) => `/admin/payments/${id}`
	},
	{
		pattern: '/admin/usage',
		title: '사용량',
		description: '서비스 사용 현황을 확인합니다.',
		domain: 'admin',
		nav: true,
		navOrder: 40,
		superAdmin: true,
		endpoint: '/admin/usage'
	},
	{
		pattern: '/admin/audit-logs',
		title: '감사 로그',
		description: '운영 콘솔 접근과 주요 작업 이력을 확인합니다.',
		domain: 'admin',
		nav: true,
		navOrder: 50,
		superAdmin: true,
		endpoint: '/admin/audit-logs?page=0&size=20'
	}
];

function normalizePath(pathname: string): string {
	if (!pathname || pathname === '/') return '/';
	return pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
}

function matchPattern(pattern: string, pathname: string): RouteParams | null {
	const patternParts = normalizePath(pattern).split('/').filter(Boolean);
	const pathParts = normalizePath(pathname).split('/').filter(Boolean);
	if (patternParts.length !== pathParts.length) return null;

	const params: RouteParams = {};
	for (let index = 0; index < patternParts.length; index += 1) {
		const patternPart = patternParts[index];
		const pathPart = pathParts[index];
		if (patternPart?.startsWith(':')) {
			params[patternPart.slice(1)] = decodeURIComponent(pathPart ?? '');
			continue;
		}
		if (patternPart !== pathPart) return null;
	}
	return params;
}

export function matchRoute(pathname: string): MatchedRoute {
	const normalized = normalizePath(pathname);
	for (const definition of ROUTES) {
		const params = matchPattern(definition.pattern, normalized);
		if (params) return { definition, params };
	}
	return {
		definition: {
			pattern: normalized,
			title: '페이지',
			description: '요청한 AntFlow 화면입니다.',
			domain: 'today',
			endpoint: undefined
		},
		params: {}
	};
}

export function resolveEndpoint(route: MatchedRoute): string | null {
	const endpoint = route.definition.endpoint;
	if (!endpoint) return null;
	return typeof endpoint === 'function' ? endpoint(route.params) : endpoint;
}

export function canAccessRoute(route: RouteDefinition, role: UserRole | null): boolean {
	if (route.public) return true;
	if (!role) return false;
	if (route.superAdmin) return role === 'SUPER_ADMIN';
	if (role === 'SUPER_ADMIN') return true;
	if (route.owner && role !== 'OWNER') return false;
	if (route.admin && role !== 'OWNER' && role !== 'ADMIN') return false;
	if (route.employee && role !== 'EMPLOYEE') return false;
	return true;
}

export function isPublicPath(pathname: string): boolean {
	return PUBLIC_PATHS.has(normalizePath(pathname));
}

function compareNavigationRoutes(left: RouteDefinition, right: RouteDefinition): number {
	const domainDifference = DOMAIN_ORDER[left.domain] - DOMAIN_ORDER[right.domain];
	if (domainDifference !== 0) return domainDifference;

	const orderDifference = (left.navOrder ?? 1000) - (right.navOrder ?? 1000);
	if (orderDifference !== 0) return orderDifference;

	return left.title.localeCompare(right.title, 'ko');
}

export function navigationRoutes(
	role: UserRole | null,
	plan?: string | null
): RouteDefinition[] {
	return ROUTES.filter(
		(route) => route.nav && canAccessRoute(route, role) && routeMeetsPlan(route, plan)
	).sort(compareNavigationRoutes);
}

export function navigationDomains(role: UserRole | null): NavigationDomainDefinition[] {
	return role === 'SUPER_ADMIN' ? ADMIN_NAV_DOMAINS : PRODUCT_NAV_DOMAINS;
}

export function navigationSections(
	role: UserRole | null,
	plan?: string | null
): NavigationSection[] {
	const routes = navigationRoutes(role, plan);
	return navigationDomains(role)
		.map((domain) => ({
			...domain,
			routes: routes.filter((route) => route.domain === domain.domain)
		}))
		.filter((section) => section.routes.length > 0);
}

function isRouteDefinition(route: RouteDefinition | undefined): route is RouteDefinition {
	return route !== undefined;
}

export function mobileNavigationRoutes(
	role: UserRole | null,
	plan?: string | null
): RouteDefinition[] {
	if (!role) return [];
	const accessibleRoutes = navigationRoutes(role, plan);
	const routesByPattern = new Map(accessibleRoutes.map((route) => [route.pattern, route]));

	return MOBILE_NAV_PATTERNS[role]
		.map((pattern) => routesByPattern.get(pattern))
		.filter(isRouteDefinition);
}
